import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/** A small yes/no dialog — destructive by default; `tone="primary"` for a plain confirmation. */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  busy,
  error,
  onConfirm,
  onClose,
  tone = "danger",
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, busy]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-sm rounded-xl bg-surface shadow-xl">
        <div className="px-5 py-4">
          <h2 className="text-sm font-semibold">{title}</h2>
          <p className="mt-2 text-sm text-ink-2">{message}</p>

          {error && (
            <div className="mt-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={
              tone === "primary"
                ? "btn-primary"
                : `rounded-lg bg-danger px-4 py-2 text-dense font-semibold text-danger-on transition
              hover:bg-danger-text disabled:cursor-not-allowed disabled:border disabled:border-line-strong disabled:bg-raised disabled:text-ink-2`
            }
          >
            {busy
              ? t(tone === "primary" ? "common.saving" : "services.deleting")
              : (confirmLabel ?? t("common.delete"))}
          </button>
        </div>
      </div>
    </div>
  );
}
